import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useServerFilters } from '@/hooks/use-server-filters';

/**
 * How one filter column maps onto the query string.
 *
 * The defaults cover the common case — a column whose id matches its query
 * parameter and which sends its selected values as an array. The optional hooks
 * exist for the handful of filters that need a different wire format.
 */
export interface FilterSpec {
    /** Query-string parameter this column writes to. */
    param: string;
    /** Selected values -> query value. Defaults to the array itself. */
    toQuery?: (values: string[]) => unknown;
    /** Additional parameters this filter owns. Cleared together with it. */
    extraParams?: (values: string[]) => Record<string, unknown>;
    /** Server `filters` prop -> selected values. Defaults to array-or-scalar coercion. */
    fromQuery?: (filters: Record<string, unknown>) => string[];
    /** Selection to fall back to when filters are cleared. Defaults to none. */
    defaultValues?: string[];
}

export type FilterSpecMap = Record<string, FilterSpec>;

type QueryValue = string | number | boolean | string[] | undefined;

/** Coerce whatever the server echoed back — scalar, number or array — into a value list. */
function defaultFromQuery(value: unknown): string[] {
    if (value === undefined || value === null || value === '') {
        return [];
    }

    if (Array.isArray(value)) {
        return value.map((entry) => String(entry)).filter((entry) => entry !== '');
    }

    return [String(value)];
}

/**
 * Index-page filter state, driven by a declarative column -> query-parameter map.
 *
 * Every filter routes to the server, so results always reflect the whole dataset
 * rather than the rows that happen to be on the current page. Because the map is
 * the single source of truth for both directions, a column id that no handler
 * knows about can no longer fail silently, and `clearFilters` cannot drift out of
 * sync with the filters that are actually applied.
 */
export function useIndexFilters<T extends Record<string, unknown>>(specs: FilterSpecMap, serverFilters: T, route: string) {
    const { applyFilters, loading } = useServerFilters(serverFilters, route);

    // Specs are declared inline at each call site, so a new object identity arrives on
    // every render. Keep them in a ref so the callbacks below stay stable.
    const specsRef = useRef(specs);
    useEffect(() => {
        specsRef.current = specs;
    }, [specs]);

    const readFromServer = useCallback((filters: Record<string, unknown>): Record<string, string[]> => {
        const next: Record<string, string[]> = {};

        for (const [columnId, spec] of Object.entries(specsRef.current)) {
            const values = spec.fromQuery ? spec.fromQuery(filters) : defaultFromQuery(filters[spec.param]);

            if (values.length > 0) {
                next[columnId] = values;
            }
        }

        return next;
    }, []);

    const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(() => readFromServer(serverFilters));

    // Re-seed from the server on every navigation so bookmarks, browser back/forward and
    // `clearFilters` all land on the same state the server actually applied.
    const serverFilterSignature = JSON.stringify(serverFilters);
    useEffect(() => {
        setColumnFilters(readFromServer(JSON.parse(serverFilterSignature) as Record<string, unknown>));
    }, [readFromServer, serverFilterSignature]);

    /** Every parameter the spec map owns, derived from a complete selection state. */
    const toQueryParams = useCallback((selection: Record<string, string[]>): Record<string, QueryValue> => {
        const params: Record<string, QueryValue> = {};

        for (const [columnId, spec] of Object.entries(specsRef.current)) {
            const values = selection[columnId] ?? [];

            params[spec.param] = (spec.toQuery ? spec.toQuery(values) : values.length > 0 ? values : undefined) as QueryValue;

            if (spec.extraParams) {
                Object.assign(params, spec.extraParams(values));
            }
        }

        return params;
    }, []);

    const handleFilterChange = useCallback(
        (columnId: string, values: string[]) => {
            const spec = specsRef.current[columnId];

            if (!spec) {
                if (import.meta.env.DEV) {
                    console.warn(
                        `[useIndexFilters] No filter spec for column "${columnId}". ` +
                            `Add it to the spec map, or remove it from columnFilterOptions so it stops rendering.`,
                    );
                }
                return;
            }

            // Derived outside the state updater: React double-invokes updaters in StrictMode,
            // and firing the Inertia visit from in there issues the request twice.
            setColumnFilters((previous) => {
                const next = { ...previous };

                if (values.length > 0) {
                    next[columnId] = values;
                } else {
                    delete next[columnId];
                }

                return next;
            });

            const nextSelection = { ...columnFilters };
            if (values.length > 0) {
                nextSelection[columnId] = values;
            } else {
                delete nextSelection[columnId];
            }

            applyFilters({ ...toQueryParams(nextSelection), page: 1 } as unknown as Partial<T>);
        },
        [applyFilters, columnFilters, toQueryParams],
    );

    const toggleSingleFilter = useCallback(
        (columnId: string, value: string) => {
            const current = columnFilters[columnId] ?? [];
            const next = current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value];

            handleFilterChange(columnId, next);
        },
        [columnFilters, handleFilterChange],
    );

    /**
     * Reset every filter this map owns, plus the search box. `extra` clears page-specific
     * params the map does not cover, such as a custom date range.
     */
    const clearFilters = useCallback(
        (extra?: Partial<T>) => {
            const defaults: Record<string, string[]> = {};

            for (const [columnId, spec] of Object.entries(specsRef.current)) {
                if (spec.defaultValues && spec.defaultValues.length > 0) {
                    defaults[columnId] = spec.defaultValues;
                }
            }

            setColumnFilters(defaults);
            applyFilters({ ...toQueryParams(defaults), search: undefined, page: 1, ...extra } as unknown as Partial<T>);
        },
        [applyFilters, toQueryParams],
    );

    const activeFilterCount = useMemo(() => Object.values(columnFilters).reduce((count, values) => count + values.length, 0), [columnFilters]);

    return {
        columnFilters,
        setColumnFilters,
        handleFilterChange,
        toggleSingleFilter,
        clearFilters,
        applyFilters,
        activeFilterCount,
        loading,
    };
}
